
// Updated by: Halimunnisa
// Change: Allowed extraData.status to override pendingStatus.targetStatus during lead updates

import React from 'react';
import { AddLead } from "./AddLead";
import { ViewLead } from "./ViewLead";
import { ChangeStatusForm } from "./ChangeStatusForm";
import { UpdateAppointmentForm } from "../../../client_contact_stages/Appointment Scheduled/components/UpdateAppointmentForm";

import { ConfirmationModal, Modal } from "@/components/ui";
import { PaymentManagement } from "../../../verve-projects-flow-final/components/overview/PaymentManagement";
import { CreditPaymentForm } from "../../../client_contact_stages/payment-advance/components/advance_payment";
import { AddDesign } from "../../../client_contact_stages/verve-design/components/AddDesign";
import { designService } from "../../../client_contact_stages/verve-design/services/designService";
import { leadUploadService } from "../../../client_contact_stages/verve-design/services/leadUploadService";
import { leadService } from "../services/leadService";
import { siteVisitService } from "../../../client_contact_stages/Site-Visit/services/siteVisitService";
import { showSuccessMessage, showErrorMessage, showApiError } from "@/lib/toast";
import API from "@/lib/API";

export const LeadsModals = ({
  // State variables
  isFormOpen,
  pendingStatus,
  isDetailOpen,
  confirmState,
  isAppointmentFormOpen,
  
  // State setters
  setIsFormOpen,
  setPendingStatus,
  setIsDetailOpen,
  setConfirmState,
  setIsAppointmentFormOpen,
  setSelectedLead,
  
  // Data
  activeLead,
  team,
  user,
  projectManagers,
  leadStages,
  
  // Methods
  handleLeadSubmit,
  refetch,
  toggleLeadStatus,
  handleUpdateLead,
  handleAppointmentSubmit,
}) => {
  const isPaymentStatus = ["advance_payment_10", "advance_payment_50", "final_payment"].includes(pendingStatus?.targetStatus);

  return (
    <>
      <AddLead
        isOpen={isFormOpen}
        onClose={() => { setIsFormOpen(false); setSelectedLead(null); }}
        onSubmit={handleLeadSubmit}
        initialData={activeLead}
        teamMembers={team}
        user={user}
      />

      <ChangeStatusForm
        isOpen={!!pendingStatus && !isPaymentStatus && !['initial_design', 'sign_of_copy', 'prod_drawings', 'interested'].includes(pendingStatus?.targetStatus)}
        onClose={() => setPendingStatus(null)}
        onConfirm={async (extraData) => {
          if (!pendingStatus?.lead?._id) return;
          
          let response;
          const finalTargetStatus = extraData.status || pendingStatus.targetStatus;
          
          console.log("[DEBUG] LeadsModals onConfirm extraData:", extraData);
          console.log("[DEBUG] LeadsModals finalTargetStatus:", finalTargetStatus);
          
          if (pendingStatus.targetStatus === 'site_visit') {
            const payload = { ...extraData, status: finalTargetStatus };
            console.log("[DEBUG] LeadsModals payload for site_visit:", payload);
            response = await siteVisitService.submitSiteVisit(pendingStatus.lead._id, payload);
          } else {
            const payload = { ...extraData, status: finalTargetStatus };
            console.log("[DEBUG] LeadsModals payload for update:", payload);
            response = await leadService.update(pendingStatus.lead._id, payload);
          }

          const emailStatus = response?.data?.emailStatus || response?.emailStatus;
          if (emailStatus === 'sent') {
            showSuccessMessage("Status updated & Quotation sent!");
          } else if (emailStatus === 'invalid_address') {
            showErrorMessage("Status updated but Address not found! Please check the email address.");
          } else if (emailStatus === 'skipped') {
            showSuccessMessage("Status updated (Email skipped: No valid address). You can download and send it manually from the View modal.");
          } else if (emailStatus === 'failed') {
            showErrorMessage("Status updated but email failed to send. Please download manually from the View modal.");
          } else {
            showSuccessMessage("Status updated successfully");
          }

          setPendingStatus(null);
          refetch();
        }}
        pendingStatus={pendingStatus}
        teamMembers={team}
        stages={leadStages}
        user={user}
      />

      {!!pendingStatus && isPaymentStatus && (
        <CreditPaymentForm
          isOpen={true}
          onClose={() => setPendingStatus(null)}
          projects={pendingStatus?.lead ? [pendingStatus.lead] : []}
          targetStatus={pendingStatus?.targetStatus}
          onConfirm={async (form) => {
            try {
              const payload = {
                amount: Number(form.amount),
                paymentStage: form.stage,
                paymentDate: form.paymentDate,
                paymentMethod: form.method,
                transactionId: form.transactionId,
                bankName: form.bankName,
                branch: form.branch,
                upiPhone: form.upiPhone,
                receiverName: form.receiverName,
                receiverPhone: form.receiverPhone,
                chequeNumber: form.chequeNumber,
                chequeDate: form.chequeDate,
                remarks: form.remarks,
                relatedProjectId: pendingStatus.lead._id,
                isFollowup: form.isFollowup || false,
                followupReason: form.followupReason || '',
                approxPaymentDate: form.approxPaymentDate || null,
                followupDescription: form.followupDescription || ''
              };

              // Post to our new endpoint
              await API.post("/lead-adv-payment", payload);

              // Update the lead status
              await leadService.update(pendingStatus.lead._id, { status: pendingStatus.targetStatus });
              showSuccessMessage("Payment recorded and status updated");
              setPendingStatus(null);
              refetch();
            } catch (err) {
              showApiError(err);
            }
          }}
        />
      )}

      {/* Initial Design Upload Modal */}
      {!!pendingStatus && ['initial_design', 'sign_of_copy', 'prod_drawings', 'interested'].includes(pendingStatus.targetStatus) && (
        <AddDesign
          isOpen={true}
          onClose={() => setPendingStatus(null)}
          initialData={pendingStatus.lead}
          targetStatus={pendingStatus.targetStatus}
          onSubmit={async (formDataArray) => {
            try {
              // Upload all design media to new backend
              await Promise.all(formDataArray.map(fd => designService.uploadMedia(fd)));
              
              // Update the lead status
              await leadService.update(pendingStatus.lead._id, { status: pendingStatus.targetStatus });
              showSuccessMessage(`Files uploaded and status updated to ${pendingStatus.targetStatus.replace('_', ' ')}`);
              setPendingStatus(null);
              refetch();
            } catch (err) {
              showApiError(err);
            }
          }}
        />
      )}

      <ViewLead
        isOpen={isDetailOpen}
        onClose={() => { setIsDetailOpen(false); setSelectedLead(null); }}
        lead={activeLead}
        user={user}
        stages={leadStages}
        teamMembers={team}
        projectManagers={projectManagers}
        updateStatus={toggleLeadStatus}
        updateLeadState={handleUpdateLead}
        onToggleDisable={async (lead) => {
          const isEnabling = lead.isDisabled;
          setConfirmState({
            isOpen: true,
            title: isEnabling ? "Enable Lead" : "Disable Lead",
            message: `Are you sure you want to ${isEnabling ? 'enable' : 'disable'} this lead record?`,
            variant: isEnabling ? "info" : "danger",
            onConfirm: async () => {
              setConfirmState(prev => ({ ...prev, loading: true }));
              try {
                await leadService.toggleDisable(lead._id, !lead.isDisabled);
                showSuccessMessage(`Lead ${isEnabling ? 'enabled' : 'disabled'} successfully`);
                refetch();
              } catch (err) { showApiError(err); } finally { setConfirmState({ isOpen: false }); }
            }
          });
        }}
      />



      <ConfirmationModal
        isOpen={confirmState.isOpen}
        onClose={() => setConfirmState({ isOpen: false })}
        onConfirm={confirmState.onConfirm}
        title={confirmState.title}
        message={confirmState.message}
        variant={confirmState.variant}
        confirmText={confirmState.confirmText}
        loading={confirmState.loading}
      />


      <UpdateAppointmentForm
        isOpen={isAppointmentFormOpen}
        onClose={() => { setIsAppointmentFormOpen(false); setSelectedLead(null); }}
        onSubmit={handleAppointmentSubmit}
        initialData={activeLead}
      />
      

    </>
  );
};
